// Packages Import
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
	Box,
	Paper,
	Tooltip,
	Typography,
	Button,
	Modal,
	Stack,
	Grid,
	RadioGroup,
	Radio,
	FormControlLabel,
	FormControl,
	Checkbox,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";

// Project Component
import ColoredTextField from "../../../UI/ColoredTextField";
import Datepicker from "../../../UI/Datepicker";
import DropdownFilter from "../../../UI/DropdownFilter";
import useHttp from "../../../../Hooks/use-http";
import FullScreenLoadingIndicator from "../../../UI/FullScreenLoadingIndicator";
import { reportsActions } from "../../../../store/Reports/Reports-slice";
import {
	APIURL,
	PDF_GEN_JWT_TOKEN_URL,
	PDF_GEN_URL,
	OSAltKey,
	isEmptyObject,
	isNumber,
	convertTrueFalse,
	storePartHistories,
} from "../../../Util/Util";
import AutocompleteDropdown from "../../../UI/AutocompleteDropdown";
import SetFileName from "../../../Util/SetFileName";
import { subsetReloadPage } from "../../Subset/CreateSubset";

const NotesActionStatusMlarReport = (props) => {
	const dispatch = useDispatch();
	const { sendRequest: fetchDataWithoutLoad } = useHttp();
	const [loading, setLoading] = useState(false);
	const [disableCheckNoteAction, setDisableCheckNoteAction] = useState(false);
	const accessToken = useSelector((state) => state.authenticationState.accessToken);
	const orgList = useSelector((state) => state.reportsState.orgList);
	const data = useSelector((state) => state.reportsState.reports.mlar.notesActionStatus);
	const showPopup = useSelector((state) => state.reportsState.popUpStates.mlar.notesActionStatus);
	const userInfo = {
		id: useSelector((state) => state.authenticationState.userNTID),
	};
	const handleError = typeof props.onError === "function" ? props.onError : () => {};
	const handleSuccess = typeof props.onSuccess === "function" ? props.onSuccess : () => {};

	const headers = { "Content-Type": "application/json", Authorization: "Bearer " + accessToken };
	const { pathname: urlPathName } = useLocation();

	const style = {
		position: "absolute",
		top: "50%",
		left: "50%",
		transform: "translate(-50%, -50%)",
		width: { xs: "100%", sm: 650 },
		maxHeight: "80%",
		bgcolor: "background.paper",
		boxShadow: 24,
		overflow: "auto",
		p: 4,
		pl: { xs: 1, sm: 4 },
		pr: { xs: 1, sm: 4 },
	};

	// Set File Name
	const [blob, setBlob] = useState(null);
	const [openSetFileName, setOpenSetFileName] = useState(false);
	const [fileName, setFileName] = useState("");

	const handleOrgChange = (value) => {
		dispatch(
			reportsActions.updateData({
				data: {
					userOrg: value,
					mtlName: {
						id: "",
						label: "",
					},
				},
			})
		);
		dispatch(reportsActions.updateMtlNameList({ data: data.originalUserList }));
		dispatch(reportsActions.setError({ data: { mtlName: true } }));
	};

	const handleJWTToken = (token, data) => {
		let filename = "";
		fetch(PDF_GEN_URL, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token.access}`,
			},
			body: JSON.stringify(data),
		})
			.then(async (response) => {
				if (!response.ok) {
					const error = await response.json();
					throw Error(error.Error ? error.Error : response.statusText ? response.statusText : "Something Went Wrong");
				}
				const disposition = response.headers.get("content-disposition");
				filename = disposition.match(/filename=(.+)/)[1];
				return response.blob();
			})
			.then((data) => {
				setBlob(data);
				setFileName(filename);
				setOpenSetFileName(true);
				setLoading(false);
			})
			.catch((error) => {
				handleError(error.toString());
				setLoading(false);
			});
	};
	const generateReport = () => {
		setLoading(true);
		closeHandler();
		const jsonReq = {
			startDate: data.startDate,
			endDate: data.endDate,
			userid: userInfo.id,
			userOrg: data.userOrg,
			mtlName: data.mtlName.id,
			checkName: convertTrueFalse(data.checkName),
			checkNoteAction: convertTrueFalse(data.checkNoteAction),
			checkDetailReport: convertTrueFalse(data.checkDetailReport),
			createSubset: convertTrueFalse(data.createSubset),
		};
		fetchDataWithoutLoad(
			{
				url: `${APIURL}reports/mlarNotesReport`,
				headers: headers,
				method: "POST",
				body: jsonReq,
			},
			(data) => {
				if (data?.No_Record) {
					throw Error(data.No_Record);
				}
				fetchDataWithoutLoad(
					{
						url: PDF_GEN_JWT_TOKEN_URL,
						headers: headers,
						method: "POST",
						body: {
							username: "admin",
							password: "pass",
						},
					},
					(token) => handleJWTToken(token, data),
					(error) => {
						setLoading(false);
						handleError(error.toString());
					}
				);
			},
			(error) => {
				setLoading(false);
				handleError(error.toString());
			}
		);
	};
	const loadSubset = (subsetData) => {
		window.sessionStorage.setItem("subsetLoaded", JSON.stringify("SUBSET"));
		const loadedSubset = subsetData[0];
		storePartHistories(loadedSubset.partnumber[0]);
		window.sessionStorage.setItem("subsetPartNumbers", JSON.stringify(loadedSubset.partnumber));
		window.sessionStorage.setItem("subsetUseItemOrder", true);
		window.sessionStorage.setItem("subsetLoaded", true);
		window.sessionStorage.setItem("subsetName", JSON.stringify(`${loadedSubset.subsetid}`));
		subsetReloadPage(urlPathName, dispatch);
	};
	const createSubset = (partNums, setLoading) => {
		const today = moment();
		const subsetId = `subset_mynotes_${today.format("YYYYMMDDHHmmss")}`;
		const jsonReq = {
			userid: userInfo.id,
			subsetid: subsetId,
			partnumber: partNums,
			createdby: "REPORTS",
			updatedby: "REPORTS",
		};
		fetchDataWithoutLoad(
			{
				url: `${APIURL}subset/editSubset`,
				headers: headers,
				method: "POST",
				body: jsonReq,
			},
			(retData) => {
				handleSuccess(`Successfully Exported to ${subsetId}`, () => {
					loadSubset(retData.Subset_Info);
				});
				setLoading(false);
			},
			(error) => {
				setLoading(false);
				handleError(error.toString());
			}
		);
	};
	const handleSubset = () => {
		setLoading(true);
		closeHandler(true);
		const mtlName = data.mtlName.label ?? "";
		const jsonReq = {
			startDate: data.startDate,
			endDate: data.endDate,
			userid: userInfo.id,
			userOrg: data.userOrg,
			mtlName: data.mtlName.id,
			checkName: convertTrueFalse(data.checkName),
			checkNoteAction: convertTrueFalse(data.checkNoteAction),
			checkDetailReport: convertTrueFalse(data.checkDetailReport),
			createSubset: convertTrueFalse(data.createSubset),
		};
		fetchDataWithoutLoad(
			{
				url: `${APIURL}reports/mlarNotesReport`,
				headers: headers,
				method: "POST",
				body: jsonReq,
			},
			(data) => {
				if (data?.No_Record) {
					throw Error(data.No_Record);
				}
				if (Array.isArray(data.partnumber) && data.partnumber.length < 1) {
					throw Error(`No Part Number found for ${mtlName}`);
				}
				createSubset(data.partnumber, setLoading);
			},
			(error) => {
				setLoading(false);
				handleError(error.toString());
			}
		);
	};

	const closeHandler = () => {
		dispatch(reportsActions.updatePopUpState());
		dispatch(reportsActions.reset({ ignore: ["mtlNameList"] }));
	};

	const handleDisableDate = (val, type) => {
		const today = moment();
		const curDateCopy = moment(val, "YYYY-MM-DD");
		const endDt = moment(data.endDate, "YYYY-MM-DD");
		switch (type) {
			case "start":
				if (endDt.diff(curDateCopy, "days") > data.maxDay || curDateCopy.isSameOrAfter(endDt)) {
					return true;
				} else {
					return false;
				}
			case "end":
				if (curDateCopy.isAfter(today)) {
					return true;
				} else {
					return false;
				}
			default:
				return true;
		}
	};

	const handleDateSelection = (val, type) => {
		let daysDifference = data.dayNum;
		let startDate = moment(data.startDate, "YYYY-MM-DD");
		let endDate = moment(data.endDate, "YYYY-MM-DD");
		const dateVal = moment(val, "YYYY-MM-DD");
		let error = false
		switch (type) {
			case "start":
				daysDifference = endDate.diff(dateVal, "days");
				startDate = dateVal;
				break;
			case "end":
				endDate = dateVal;
				const daysDiff = dateVal.diff(startDate, "days");
				if (daysDiff > data.maxDay) {
					daysDifference = data.maxDay;
					startDate = moment(endDate, "YYYY-MM-DD").subtract(daysDifference, "day");
				} else if (daysDiff < 1) {
					daysDifference = data.dayNum;
					error = true
				} else {
					daysDifference = daysDiff;
				}
				break;
			default:
				break;
		}
		dispatch(reportsActions.setError({ data: { startDate: error } }));
		dispatch(
			reportsActions.updateData({
				data: {
					startDate: startDate.format("YYYY-MM-DD"),
					endDate: endDate.format("YYYY-MM-DD"),
					dayNum: daysDifference,
				},
			})
		);
	};

	const handleDayNum = (event) => {
		const inpVal = event.target.value;
		if (isNumber(inpVal) || inpVal === "") {
			const val = inpVal < 1 ? 1 : inpVal > data.maxDay ? data.maxDay : inpVal;
			const endDate = moment(data.endDate, "YYYY-MM-DD");
			const startDate = endDate.subtract(val, "day").format("YYYY-MM-DD");
			if (inpVal === "") {
				dispatch(
					reportsActions.updateData({
						data: {
							dayNum: inpVal,
						},
					})
				);
			} else {
				dispatch(
					reportsActions.updateData({
						data: {
							dayNum: val,
							startDate: startDate,
						},
					})
				);
			}
		}
	};

	const handleRadioPdfSubset = (event) => {
		const val = event.target.value;
		let createSubset = false;
		if (val === "subset") {
			createSubset = true;
			dispatch(
				reportsActions.updateData({
					data: {
						checkNoteAction: true,
					},
				})
			);
		}
		dispatch(
			reportsActions.updateData({
				data: {
					export: val,
					createSubset: createSubset,
				},
			})
		);
	};

	const handleCheckbox = (type) => (event) => {
		let extraData = {};
		switch (type) {
			case "checkNoteAction":
				break;
			case "checkDetailReport":
				extraData = {
					checkNoteAction: event.target.checked,
				};
				setDisableCheckNoteAction(event.target.checked);
				break;
			case "checkName":
				extraData = {
					mtlName: {
						id: "",
						label: "",
					},
				};
				dispatch(reportsActions.setError({ data: { mtlName: event.target.checked } }));
				break;
			default:
				break;
		}
		dispatch(
			reportsActions.updateData({
				data: {
					[type]: event.target.checked,
					...extraData,
				},
			})
		);
	};
	const handleSelection = (val, type) => {
		if (val === null) {
			dispatch(reportsActions.setError({ data: { mtlName: true } }));
		} else {
			dispatch(reportsActions.updateData({ data: { mtlNameTyped: val, mtlName: val } }));
			dispatch(reportsActions.setError({ data: { mtlName: false } }));
		}
	};
	const handleInputChange = (val, type) => {
		if (data.mtlNameList.length > 0 && !isEmptyObject(data.mtlNameList[0])) {
			let userid = "";
			let isExist;
			isExist = data.mtlNameList.findIndex((singleData) => {
				if (singleData.label === val) {
					userid = singleData.id;
					return true;
				}
				return false;
			});
			if (isExist > -1) {
				dispatch(reportsActions.updateData({ data: { mtlNameTyped: val, mtlName: { id: userid, label: val } } }));
				dispatch(reportsActions.setError({ data: { mtlName: false } }));
			} else {
				dispatch(reportsActions.updateData({ data: { mtlNameTyped: val } }));
				dispatch(reportsActions.setError({ data: { mtlName: true } }));
			}
		}
	};
	return (
		<Box>
			<FullScreenLoadingIndicator loading={loading} />
			<SetFileName
				blob={blob}
				setOpenSetFileName={setOpenSetFileName}
				openSetFileName={openSetFileName}
				fileName={fileName}
			/>
			<Modal
				open={showPopup}
				aria-labelledby="modal-modal-title"
				aria-describedby="modal-modal-description"
				disableScrollLock
			>
				<Box
					sx={style}
					onKeyDown={(e) => {
						if (e.altKey) {
							if (e.code === "KeyC") {
								closeHandler();
							}
							if (!data.error.mtlName && !data.error.startDate) {
								if (e.code === "KeyP" && data.export === "pdf") {
									generateReport();
								}
								if (e.code === "KeyS" && data.export === "subset") {
									handleSubset();
								}
							}
						}
					}}
				>
					<Stack mb={2} spacing={1}>
						<Box sx={{ alignItems: "center", display: "flex", width: "97%" }}>
							<Typography variant="h5" sx={{ textAlign: "center", width: "100%" }}>
								Mlar Status Report
							</Typography>
							<DropdownFilter
								width={90}
								selections={orgList}
								selectedData={data.userOrg}
								handleSelection={handleOrgChange}
								label={"User Org"}
								InputLabelProps={{
									shrink: true,
									sx: {
										fontWeight: 700,
									},
								}}
							/>
						</Box>
						<Paper sx={{ p: 2 }} elevation={5}>
							<Typography variant="subtitle">
								{`Engineering status report will give you the count of all the parts involved into MLARs for the last 4
								weeks(default entry is 28 days). It will give the number count per Materials Engineer name. Detail
								report lists all the effected PN's and give you ability to create a SUBSET`}
							</Typography>
						</Paper>
						<Grid
							container
							direction="row"
							spacing={0}
							sx={{
								pt: 4,
							}}
						>
							<Grid
								item
								xl={5}
								lg={5}
								md={5}
								sm={5}
								xs={12}
								sx={{
									pr: { xs: 0, sm: 1, md: 1, lg: 1, xl: 1 },
								}}
							>
								<Datepicker
									label="Start Date"
									handleSelection={handleDateSelection}
									val={data.startDate}
									name="start"
									disableFuture={false}
									disablePast={false}
									shouldDisableDate={handleDisableDate}
									InputLabelProps={{
										shrink: true,
										sx: {
											fontWeight: 700,
										},
									}}
								/>
							</Grid>
							<Grid
								item
								xl={5}
								lg={5}
								md={5}
								sm={5}
								xs={12}
								sx={{
									pr: { xs: 0, sm: 1, md: 1, lg: 1, xl: 1 },
									pt: { xs: 1.5, sm: 0, md: 0, lg: 0, xl: 0 },
								}}
							>
								<Datepicker
									label="End Date"
									handleSelection={handleDateSelection}
									val={data.endDate}
									name="end"
									disableFuture={false}
									disablePast={false}
									shouldDisableDate={handleDisableDate}
									InputLabelProps={{
										shrink: true,
										sx: {
											fontWeight: 700,
										},
									}}
								/>
							</Grid>
							<Grid
								item
								xl={2}
								lg={2}
								md={2}
								sm={2}
								xs={12}
								sx={{
									pt: { xs: 1.5, sm: 0, md: 0, lg: 0, xl: 0 },
								}}
							>
								<ColoredTextField
									label="Days"
									fullWidth
									focused
									onChange={handleDayNum}
									value={data.dayNum}
									InputLabelProps={{
										shrink: true,
										sx: {
											fontWeight: 700,
										},
									}}
								/>
							</Grid>
						</Grid>
						<Typography variant="caption" sx={{ fontWeight: 700 }}>
							{`Start Date cannot be more than 6 months(${data.maxDay} days) from End Date`}
						</Typography>
						<Grid
							container
							direction="row"
							spacing={0}
							sx={{
								pt: 0,
							}}
						>
							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<FormControl>
									<RadioGroup row onChange={handleRadioPdfSubset} value={data.export}>
										<FormControlLabel value="pdf" control={<Radio />} label="Print to PDF" />
										<FormControlLabel value="subset" control={<Radio />} label="Export to Subset" />
									</RadioGroup>
								</FormControl>
							</Grid>

							<Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
								<FormControlLabel
									label="Include ONLY parts that require action"
									control={<Checkbox disabled={disableCheckNoteAction} checked={data.checkNoteAction} />}
									onChange={handleCheckbox("checkNoteAction")}
								/>
								<FormControlLabel
									label="PRINT detail report with PN's"
									control={<Checkbox checked={data.checkDetailReport} />}
									onChange={handleCheckbox("checkDetailReport")}
								/>
							</Grid>
							<Grid item xl={5} lg={5} md={12} sm={12} xs={12}>
								<FormControlLabel
									label="Choose MTL Name or uncheck for ALL MTL Name"
									control={<Checkbox checked={data.checkName} />}
									onChange={handleCheckbox("checkName")}
								/>
							</Grid>
							<Grid
								item
								xl={7}
								lg={7}
								md={12}
								sm={12}
								xs={12}
								sx={{
									pt: 1,
								}}
							>
								<AutocompleteDropdown
									label="Choose MTL Name"
									selections={data.mtlNameList}
									value={data.mtlName}
									handleSelection={handleSelection}
									handleInputChange={handleInputChange}
									name="name"
									disabled={!data.checkName}
									freeSolo={true}
									error={data.error.mtlName}
									extraProps={{
										ListboxProps: {
											sx: {
												"& .MuiAutocomplete-option.Mui-focused": {
													backgroundColor: "rgba(230, 230, 250, 0.8)",
												},
											},
										},
									}}
								/>
							</Grid>
						</Grid>
						<Grid
							container
							direction="row"
							spacing={0}
							sx={{
								pt: 1,
							}}
						>
							<Grid
								item
								xl={6.1}
								lg={6.1}
								md={6.1}
								sm={6.1}
								xs={6.1}
								sx={{
									pr: 1,
								}}
							>
								<Tooltip title={`For shortcut press ${OSAltKey} + C`}>
									<span>
										<Button sx={{ width: "100%", ml: 0 }} onClick={closeHandler}>
											<u style={{ textDecorationThickness: "2.5px", fontWeight: "bold" }}>C</u>ancel
										</Button>
									</span>
								</Tooltip>
							</Grid>
							<Grid item xl={5.9} lg={5.9} md={5.9} sm={5.9} xs={5.9}>
								{data.export === "pdf" ? (
									<Tooltip title={`For shortcut press ${OSAltKey} + P`}>
										<span>
											<Button sx={{ width: "100%", ml: 0 }} onClick={generateReport} disabled={data.error.mtlName || data.error.startDate}>
												<u style={{ textDecorationThickness: "2.5px", fontWeight: "bold" }}>P</u>rint PDF
											</Button>
										</span>
									</Tooltip>
								) : (
									<Tooltip title={`For shortcut press ${OSAltKey} + S`}>
										<span>
											<Button sx={{ width: "100%", ml: 0 }} onClick={handleSubset} disabled={data.error.mtlName || data.error.startDate}>
												Create&nbsp;<u style={{ textDecorationThickness: "2.5px", fontWeight: "bold" }}>S</u>ubset
											</Button>
										</span>
									</Tooltip>
								)}
							</Grid>
						</Grid>
						<Typography sx={{ fontSize: "0.7rem" }}>{`Shortcut: ${OSAltKey} + Hotkey`}</Typography>
					</Stack>
				</Box>
			</Modal>
		</Box>
	);
};

export default NotesActionStatusMlarReport;
